import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TextOverflowTooltip } from 'src/common';
import { ControlWithScenarios } from './types';
import { getControlId } from '../../util';
import _ from 'lodash';
import { calculateSelectionState, getCheckboxState } from './util';
import { ControlsRmpQuery } from 'src/graphql/_generated';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';

type UseColumns = (a: {
  data: ControlWithScenarios[];
  selectedControls: Record<string, Record<string, boolean>>;
  setFieldValue: (field: string, value: any) => void;
  rmpControls: ControlsRmpQuery['controls'];
}) => {
  columns: ColumnDef<ControlWithScenarios>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['frameworkShortname', 'control_identifier', 'name', 'FrameworkAuthor'];

export const useColumns: UseColumns = ({ data = [], selectedControls, setFieldValue, rmpControls }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.wizardRecomendedControls}-columns`,
    defaultColumns,
  );

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<ControlWithScenarios>[] = [
      {
        id: GTInternalIds.checker,
        header: () => {
          const allScenarios = _.flatMap(data, (controlWithScenarios) => {
            const controlId = getControlId(controlWithScenarios.control);
            const controlSelections = selectedControls[controlId] || {};

            return _.map(controlWithScenarios.scenarios || [], (scenario) => ({
              ...scenario,
              isSelected: controlSelections[scenario.scenario.id] === true,
            }));
          });

          const linkedCount = _.sumBy(allScenarios, (s) => (s.isLinked ? 1 : 0));
          const selectedCount = _.sumBy(allScenarios, (s) => (s.isSelected ? 1 : 0));
          const availableCount = _.sumBy(allScenarios, (s) => (s.isLinked ? 0 : 1));

          const checkboxState = getCheckboxState(linkedCount + selectedCount, allScenarios.length, availableCount);

          const handleHeaderClick = () => {
            const shouldSelectAll = !checkboxState.isChecked;
            const updatedSelectedControls = _.cloneDeep(selectedControls);

            data.forEach((controlWithScenarios) => {
              const controlId = getControlId(controlWithScenarios.control);
              const availableScenarios = _.filter(controlWithScenarios.scenarios || [], (s) => !s.isLinked);

              availableScenarios.forEach((scenario) => {
                _.set(updatedSelectedControls, [controlId, scenario.scenario.id], shouldSelectAll);
              });
            });

            setFieldValue('selectedControls', updatedSelectedControls);
          };

          return <GTCheckBox {...checkboxState} onClick={handleHeaderClick} />;
        },
        cell: ({ row }) => {
          const controlId = getControlId(row.original.control);
          const scenarios = row.original.scenarios || [];
          const controlSelections = selectedControls[controlId] || {};

          const selectionState = calculateSelectionState(scenarios, controlSelections);
          const checkboxState = getCheckboxState(
            selectionState.totalSelectedCount,
            selectionState.totalScenariosCount,
            selectionState.availableScenariosCount,
          );

          const handleControlClick = () => {
            const availableScenarios = _.filter(scenarios, (s) => !s.isLinked);
            const shouldSelectAll = !checkboxState.isChecked;

            const newControlSelections = _.fromPairs(
              _.map(availableScenarios, (scenario) => [scenario.scenario.id, shouldSelectAll]),
            );

            const updatedSelectedControls = _.set(_.cloneDeep(selectedControls), controlId, {
              ...controlSelections,
              ...newControlSelections,
            });

            setFieldValue('selectedControls', updatedSelectedControls);
          };

          return <GTCheckBox {...checkboxState} onClick={handleControlClick} />;
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        // @ts-ignore
        accessorFn: (row) => row.control.id || row.control.ux_id,
        cell: (info) => info.getValue(),
      },
      {
        id: 'frameworkShortname',
        header: 'Framework',
        // @ts-ignore
        accessorFn: (row) => row.control.frameworkShortname || row.control.frameworkLibrary_shortname,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'control_identifier',
        header: 'Framework Control ID',
        // @ts-ignore
        accessorFn: (row) => row.control.control_identifier || row.control.controlId,
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'control.name',
        cell: (info) => {
          const control = info.row.original.control as any;
          const allMatches = rmpControls.filter((rmpControl) => {
            const frameworkControls = rmpControl.frameworkControls || [];
            return frameworkControls.some((frameworkControl) => {
              const rmpFrameworkLibraryId = frameworkControl?.control_identifier;
              return (
                rmpFrameworkLibraryId === control.controlId || rmpFrameworkLibraryId === control.control_identifier
              );
            });
          });
          const rmpControl = allMatches[0];
          const isRmp = !!rmpControl;

          return (
            <TextOverflowTooltip>
              <Link
                className="table-link"
                to={
                  isRmp
                    ? `/risk-management/control/${rmpControl.id}`
                    : `/control-frameworks/all-controls/${info.row.original.control.ux_id}`
                }
                target={'_blank'}
                onClick={(e) => {
                  e.stopPropagation();
                  mpEvent(MPEvents.Link, {
                    source: {
                      value: ['Framework'],
                    },
                    destination: {
                      value: ['Framework Controls all'],
                      params: {
                        // @ts-ignore
                        id: info.row.original.control.ux_id || info.row.original.control.controlLibrary_Id,
                      },
                    },
                    tags: ['FRAMEWORK'],
                  });
                }}
              >
                {info.getValue() as string}
              </Link>
            </TextOverflowTooltip>
          );
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'FrameworkAuthor',
        header: 'Author',
        accessorFn: (row) => row.control.FrameworkAuthor || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, rmpControls, selectedControls]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.wizardRecomendedControls}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'frameworkShortname', title: 'Framework' },
        { key: 'control_identifier', title: 'Framework Control ID' },
        { key: 'name', title: 'Name' },
        { key: 'FrameworkAuthor', title: 'Author' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
